import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, Crown, ShieldCheck, Shield, Trash2, Gift, X, Download } from "lucide-react";

const FILTERS = [
  { id: "", label: "All" },
  { id: "pro", label: "Pro" },
  { id: "free", label: "Free" },
  { id: "comped", label: "Comped" },
  { id: "admin", label: "Admins" },
];

function statusBadge(u) {
  if (u.comped) return <Badge className="rounded-sm bg-accent text-accent-foreground">Comped</Badge>;
  if (u.subscription_status === "active") return <Badge className="rounded-sm bg-primary text-primary-foreground">Pro</Badge>;
  if (u.subscription_status === "trialing") return <Badge variant="outline" className="rounded-sm">Trial</Badge>;
  return <Badge variant="outline" className="rounded-sm text-muted-foreground">Free</Badge>;
}

export default function AdminUsers({ currentUserId }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    const params = { page, page_size: 20 };
    if (q) params.q = q;
    if (filter) params.status = filter;
    api.get("/admin/users", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [q, filter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, filter]);

  const patchUser = async (id, body, okMsg) => {
    setBusy(id);
    try {
      const { data: updated } = await api.patch(`/admin/users/${id}`, body);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.user_id === id ? { ...x, ...updated } : x)) }));
      toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };

  const removeUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      setData((d) => ({ ...d, items: d.items.filter((x) => x.user_id !== id), total: d.total - 1 }));
      toast.success("User deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (filter) params.status = filter;
      const res = await api.get("/admin/users/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "nc-legal-forms-users.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Users exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="font-serif text-4xl tracking-tight mb-1">Users</h1>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-muted-foreground">{data.total} total. Grant Pro access, manage admins, or remove accounts.</p>
        <Button onClick={exportCsv} variant="outline" size="sm" data-testid="export-users-btn" className="rounded-sm gap-2">
          <Download size={15} /> Export CSV
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="rounded-sm h-11 pl-10" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`user-filter-${f.id || "all"}`}
              className={`px-3 py-2 rounded-sm text-sm font-medium transition-colors ${filter === f.id ? "bg-primary text-primary-foreground" : "bg-white border border-border hover:bg-secondary"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-sm overflow-hidden bg-white divide-y divide-border">
        {data.items.map((u) => {
          const isSelf = u.user_id === currentUserId;
          const isAdmin = u.role === "admin";
          return (
            <div key={u.user_id} className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3" data-testid={`admin-user-row-${u.user_id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{u.name || "—"}</span>
                  {statusBadge(u)}
                  {isAdmin && <Badge variant="outline" className="rounded-sm gap-1 border-accent text-accent"><ShieldCheck size={11} /> Admin</Badge>}
                </div>
                <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                <p className="text-[0.7rem] text-muted-foreground mt-0.5">
                  {u.submission_count} forms · {u.auth_provider} · joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant={u.comped ? "outline" : "default"} disabled={busy === u.user_id}
                  data-testid={`user-comp-btn-${u.user_id}`}
                  onClick={() => patchUser(u.user_id, { comped: !u.comped }, u.comped ? "Pro access revoked" : "Pro access granted")}
                  className={`rounded-sm gap-1 h-8 ${u.comped ? "" : "bg-accent text-accent-foreground hover:bg-accent/90"}`}>
                  {u.comped ? <><X size={13} /> Revoke Pro</> : <><Gift size={13} /> Grant Pro</>}
                </Button>
                <Button size="sm" variant="outline" disabled={busy === u.user_id}
                  data-testid={`user-role-btn-${u.user_id}`}
                  onClick={() => patchUser(u.user_id, { role: isAdmin ? "user" : "admin" }, isAdmin ? "Admin removed" : "Promoted to admin")}
                  className="rounded-sm gap-1 h-8">
                  {isAdmin ? <><Shield size={13} /> Remove admin</> : <><Crown size={13} /> Make admin</>}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" disabled={isSelf} data-testid={`user-delete-btn-${u.user_id}`}
                      className="rounded-sm h-8 text-muted-foreground hover:text-accent disabled:opacity-30">
                      <Trash2 size={15} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {u.email}?</AlertDialogTitle>
                      <AlertDialogDescription>This permanently deletes the user and all their saved forms. This can’t be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => removeUser(u.user_id)} data-testid={`user-delete-confirm-${u.user_id}`} className="bg-accent text-accent-foreground hover:bg-accent/90">Delete user</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
        {data.items.length === 0 && <p className="px-4 py-8 text-center text-muted-foreground text-sm">No users match.</p>}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-sm">Prev</Button>
          <span className="text-sm text-muted-foreground px-3">{data.page} / {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="rounded-sm">Next</Button>
        </div>
      )}
    </div>
  );
}
